import { useEffect, useRef, useState } from "react";
import { Boxes, Building2, HardDrive, Search, Ticket, User } from "lucide-react";
import { Input } from "@/components/ui/primitives";
import { useDebounced, useGlobalSearch } from "./hooks";
import type { SearchHit } from "./api";

/** The view an entity type opens when a result is picked. */
export type SearchTarget = "tickets" | "customers" | "users" | "assets" | "inventory";

const GROUP_META: Record<
  string,
  { label: string; icon: typeof Ticket; target: SearchTarget }
> = {
  tickets: { label: "Tickets", icon: Ticket, target: "tickets" },
  customers: { label: "Customers", icon: Building2, target: "customers" },
  users: { label: "People", icon: User, target: "users" },
  assets: { label: "Assets", icon: HardDrive, target: "assets" },
  products: { label: "Inventory", icon: Boxes, target: "inventory" },
};

const GROUP_ORDER = ["tickets", "customers", "assets", "products", "users"];

/**
 * Header global search: trigram search across tickets, customers, people,
 * assets and stock. Results are grouped; picking one navigates to that
 * entity's section (the app uses a manual view switch, not a router).
 */
export function GlobalSearch({ onNavigate }: { onNavigate: (target: SearchTarget) => void }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(text);
  const { data, isFetching } = useGlobalSearch(debounced);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Close on click-outside and on Escape.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  function pick(hit: SearchHit) {
    const target = GROUP_META[`${hit.type}s`]?.target;
    setOpen(false);
    setText("");
    if (target) onNavigate(target);
  }

  const groups = data?.groups ?? {};
  const orderedGroups = GROUP_ORDER.filter((g) => (groups[g]?.length ?? 0) > 0);
  const showPanel = open && debounced.trim().length >= 2;

  return (
    <div ref={boxRef} className="relative hidden md:block">
      <Search
        className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        aria-label="Search everything"
        placeholder="Search everything…"
        className="w-64 pl-8"
        value={text}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
      />

      {showPanel && (
        <div
          role="listbox"
          aria-label="Search results"
          className="absolute right-0 z-50 mt-1 max-h-[70vh] w-80 overflow-y-auto rounded-md border border-border bg-card p-1 text-card-foreground shadow-lg"
        >
          {orderedGroups.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {isFetching ? "Searching…" : "No matches."}
            </p>
          ) : (
            orderedGroups.map((g) => {
              const meta = GROUP_META[g];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <div key={g} className="mb-1">
                  <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium uppercase text-muted-foreground">
                    <Icon className="h-3 w-3" aria-hidden="true" />
                    {meta.label}
                  </div>
                  {(groups[g] ?? []).map((hit) => (
                    <button
                      key={hit.id}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => pick(hit)}
                      className="flex w-full items-baseline gap-2 rounded-md px-3 py-1.5 text-left text-sm hover:bg-accent"
                    >
                      <span className="truncate font-medium">{hit.primary}</span>
                      <span className="ml-auto shrink-0 truncate text-xs text-muted-foreground">
                        {hit.secondary}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
